// The Makao table (.ai/makao.md): the deal of makao/rules.ts, the 3 computer players' timing (one action a beat, so a
// turn of several cards, a draw or a sit-out can be followed), the player's turn (lay, more of the rank, the jack's
// rank or the ace's suit, draw, keep the drawn card), "Makao!" said for a seat left with one card, and the clock. The
// computer waits while the Pause window is open or the tab is hidden. No Undo (as in Kierki).
import { useSyncExternalStore } from 'react';
import {
  applyAction, botAction, canAdd, cardKey, choiceNeeded, drawCard, finishTurn, lay, mustWait, pass, skipTurn, startGame,
  type BotAction, type Choice, type HCard, type MakaoState,
} from './rules';
import { t } from '../i18n';
import { SLOW } from '../hearts/store';
import { drawNames, fixedNames, nameList, type SeatNames } from '../hearts/names';

/** A computer player's first action of a turn, and each card more of the same rank. */
export const BOT_MS = 900;
export const BOT_MORE_MS = 450;
/** A seat that sits out (4s) shows it this long. */
export const WAIT_MS = 1100;
/** The deal (MakaoTable.tsx useDealAnimation): 20 cards round the table, then the first card turned up. */
export const DEAL_STEP_MS = 90;
export const DEAL_FLIGHT_MS = 380;
export const DEAL_MS = 19 * DEAL_STEP_MS + DEAL_FLIGHT_MS + 500;
/** "Makao!" stays over the seat this long. */
export const SAID_MS = 1800;

/** What a seat did last, for the hint and the seat's own bubble. */
export interface Happened {
  readonly seat: number;
  readonly what: 'lay' | 'draw' | 'take' | 'wait' | 'keep';
  /** Cards drawn or taken, turns to wait. */
  readonly n: number;
}

export interface TableState {
  readonly game: MakaoState | null;
  /** Seconds of play in this deal (the status bar's clock). */
  readonly seconds: number;
  /** Counts the deals shown (the view's deal animation starts on a change). */
  readonly deal: number;
  /** The deal is on screen now: taps and the computer wait. */
  readonly dealing: boolean;
  /** The player's turn waits for a jack's rank or an ace's suit. */
  readonly choosing: 'rank' | 'suit' | null;
  /** The seat that said "Makao!" last (a bubble over it for SAID_MS), with a counter so the view restarts it. */
  readonly said: { readonly seat: number; readonly n: number } | null;
  readonly last: Happened | null;
  /** The cards the player laid in this deal (a win's `moves`). */
  readonly laidByPlayer: number;
  /** A position put in by the e2e hook (load): its end is never saved as a win. */
  readonly demo: boolean;
  readonly names: SeatNames | null;
  /** When the deal began and ended (Date.now(); the end window's New game: hearts/nextGame.ts). */
  readonly startedAt: number;
  readonly endedAt: number | null;
  /** Online (online/): when my move runs out (Date.now() ms), then the server's bot plays it; unset offline. */
  readonly deadlineAt?: number | null;
}

export interface MakaoStore {
  getState(): TableState;
  subscribe(fn: () => void): () => void;
  /** A new deal: `pattern` (52 numbers, engine/shuffle.ts) gives it (Retry, a report). */
  newGame(pattern: readonly number[] | null): void;
  /** The player lays `card` (the first of a turn, one more of the rank, or the drawn one). */
  lay(card: HCard): void;
  /** The player ends a turn with more cards of the rank in hand ("Gotowe"). */
  finish(): void;
  /** The jack's rank (null: ask for nothing) or the ace's suit. */
  choose(choice: Choice): void;
  /** The player draws (or takes the open attack, or sits out a 4). */
  draw(): void;
  /** The player keeps the drawn card. */
  pass(): void;
  stop(): void;
  /** e2e only: show a position as if the deal had reached it. */
  load(g: MakaoState): void;
}

export interface MakaoHooks {
  rng: () => number;
  paused: () => boolean;
  onStep?: (what: string) => void;
  /** The deal ended (someone went out). */
  onEnd?: (s: MakaoState) => void;
}

export function createMakaoStore(hooks: MakaoHooks): MakaoStore {
  let state: TableState = { game: null, seconds: 0, deal: 0, dealing: false, choosing: null, said: null, last: null, laidByPlayer: 0, demo: false, names: null, startedAt: 0, endedAt: null };
  const listeners = new Set<() => void>();
  let timer: ReturnType<typeof setTimeout> | null = null;
  let clock: ReturnType<typeof setInterval> | null = null;
  let saidTimer: ReturnType<typeof setTimeout> | null = null;
  const set = (patch: Partial<TableState>) => {
    state = { ...state, ...patch };
    for (const fn of listeners) fn();
  };
  const step = (what: string) => hooks.onStep?.(what);
  const clearTimer = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };
  /** Runs `fn` after `ms`, later again while the game is paused. */
  const after = (ms: number, fn: () => void) => {
    clearTimer();
    timer = setTimeout(function run() {
      if (hooks.paused()) {
        timer = setTimeout(run, 300);
        return;
      }
      timer = null;
      fn();
    }, ms * SLOW);
  };
  const startClock = () => {
    if (clock !== null) return;
    clock = setInterval(() => {
      const g = state.game;
      if (!g || g.phase === 'over' || state.dealing || hooks.paused()) return;
      set({ seconds: state.seconds + 1 });
    }, 1000);
  };
  const stopClock = () => {
    if (clock !== null) clearInterval(clock);
    clock = null;
  };

  /** What `seat`'s step from `before` to `g` was, for the hint. */
  const happened = (before: MakaoState, g: MakaoState, seat: number, a: BotAction['type']): Happened | null => {
    const got = g.hands[seat]!.length - before.hands[seat]!.length;
    if (a === 'lay') return { seat, what: 'lay', n: 1 };
    if (a === 'skip') return { seat, what: 'wait', n: before.waits[seat]! };
    if (a === 'pass') return { seat, what: 'keep', n: 1 };
    if (a === 'draw') {
      if (before.attack?.kind === 'wait') return { seat, what: 'wait', n: before.attack.count };
      if (before.attack?.kind === 'draw') return { seat, what: 'take', n: got };
      return { seat, what: 'draw', n: got };
    }
    return state.last;
  };

  /** After every change: "Makao!", the end, the computer's next action, the player's sit-out or choice. */
  const advance = (g: MakaoState, before: MakaoState | null = state.game) => {
    const said = before && g.phase === 'play' ? [0, 1, 2, 3].find((p) => g.hands[p]!.length === 1 && before.hands[p]!.length > 1) : undefined;
    set({ game: g, choosing: null });
    if (said !== undefined) {
      step(`makao: seat ${said}`);
      set({ said: { seat: said, n: (state.said?.n ?? 0) + 1 } });
      if (saidTimer !== null) clearTimeout(saidTimer);
      saidTimer = setTimeout(() => set({ said: null }), SAID_MS * SLOW);
    }
    if (g.phase === 'over') {
      clearTimer();
      set({ endedAt: Date.now() });
      step(`deal over: seat ${g.winner} went out; cards left ${g.hands.map((h) => h.length).join(' ')}`);
      hooks.onEnd?.(g);
      return;
    }
    const seat = g.turn;
    if (seat !== 0) {
      const a0 = botAction(g, seat);
      const ms = a0.type === 'skip' ? WAIT_MS : g.laid.length || g.drawn ? BOT_MORE_MS : BOT_MS;
      after(ms, () => {
        const cur = state.game!;
        if (cur.phase !== 'play' || cur.turn !== seat) return;
        const a = botAction(cur, seat);
        const next = applyAction(cur, seat, a, hooks.rng);
        if (!next) {
          step(`bot ${seat} action refused: ${a.type}`);
          return;
        }
        set({ last: happened(cur, next, seat, a.type) });
        advance(next, cur);
      });
      return;
    }
    // the player sits out a 4 by itself (nothing to choose)
    if (mustWait(g, 0)) {
      set({ last: { seat: 0, what: 'wait', n: g.waits[0]! } });
      after(WAIT_MS, () => {
        const cur = state.game!;
        const next = cur.turn === 0 ? skipTurn(cur, 0) : null;
        if (next) advance(next, cur);
      });
      return;
    }
    // the player laid and no card of the rank is left: the turn ends, after the jack's or the ace's choice
    if (g.laid.length && !canAdd(g)) {
      const ask = choiceNeeded(g);
      if (ask) set({ choosing: ask });
      else advance(finishTurn(g)!, g);
    }
  };

  /** A new deal: shown at once (the view throws the cards out), played on once the deal is over. */
  const dealt = (g: MakaoState) => {
    set({ game: g, deal: state.deal + 1, dealing: true, choosing: null, said: null, last: null, laidByPlayer: 0 });
    after(DEAL_MS, () => {
      set({ dealing: false });
      advance(state.game ?? g, null);
    });
  };

  const mine = () => {
    const g = state.game;
    return g && g.phase === 'play' && g.turn === 0 && !state.dealing ? g : null;
  };

  return {
    getState: () => state,
    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    newGame(pattern) {
      clearTimer();
      set({ seconds: 0, demo: false, startedAt: Date.now(), endedAt: null, names: drawNames(hooks.rng, nameList(t('hearts.names')), t('hearts.you')) ?? fixedNames(t) });
      const g = startGame(pattern, hooks.rng);
      step(`makao deal, first card ${cardKey(g.pile[0]!)}`);
      startClock();
      dealt(g);
    },
    lay(card) {
      const g = mine();
      if (!g || state.choosing) return;
      const next = lay(g, 0, card);
      if (!next) {
        step(`card refused ${cardKey(card)}`);
        return;
      }
      step(`laid ${cardKey(card)}`);
      set({ laidByPlayer: state.laidByPlayer + 1, last: { seat: 0, what: 'lay', n: 1 } });
      advance(next, g);
    },
    finish() {
      const g = mine();
      if (!g || !g.laid.length || state.choosing) return;
      const ask = choiceNeeded(g);
      if (ask) return set({ choosing: ask });
      const next = finishTurn(g);
      if (next) advance(next, g);
    },
    choose(choice) {
      const g = mine();
      if (!g || !state.choosing) return;
      const next = finishTurn(g, choice);
      if (!next) return;
      step(`chose ${choice.suit ?? choice.rank ?? 'nothing'}`);
      advance(next, g);
    },
    draw() {
      const g = mine();
      if (!g || state.choosing) return;
      const next = drawCard(g, 0, hooks.rng);
      if (!next) return;
      const h = happened(g, next, 0, 'draw');
      step(`drew: ${h?.what} ${h?.n}`);
      set({ last: h });
      advance(next, g);
    },
    pass() {
      const g = mine();
      if (!g) return;
      const next = pass(g, 0);
      if (!next) return;
      set({ last: { seat: 0, what: 'keep', n: 1 } });
      advance(next, g);
    },
    stop() {
      clearTimer();
      stopClock();
    },
    load(g) {
      clearTimer();
      set({ demo: true, dealing: false, choosing: null, said: null, last: null });
      advance(g, null);
    },
  };
}

let current: MakaoStore | null = null;
export const setMakaoStore = (s: MakaoStore) => {
  current = s;
};
export const makaoStore = (): MakaoStore => {
  if (!current) throw new Error('makaoStore: not created');
  return current;
};
export function useMakao(): TableState {
  const s = makaoStore();
  return useSyncExternalStore(s.subscribe, s.getState, s.getState);
}

/** The table as text for a report: hands (the computer's too: a report is for us), the pile's top, the stock, the open
 *  attack and demand. */
export function makaoBoardText(st: TableState): Record<string, unknown> | null {
  const g = st.game;
  if (!g) return null;
  const k = (cards: readonly HCard[]) => cards.map(cardKey).join(' ');
  return {
    phase: g.phase, turn: g.turn, winner: g.winner, hands: g.hands.map(k), pile: k(g.pile.slice(-5)), stock: g.stock.length,
    attack: g.attack, demand: g.demand, waits: g.waits, laid: k(g.laid), drawn: g.drawn ? cardKey(g.drawn) : null,
    names: (st.names ?? fixedNames(t)).name.slice(1).join(', '), pattern: g.pattern.join(','),
  };
}
